<template>
  <template v-if="newHomeEnabled">
    <div
      class="bg-primary-950 bg-radial-[at_-5%_-5%] from-accent/30 pb-20 pt-12"
    >
      <app-page-wrapper>
        <div
          class="grid items-center gap-16 text-white lg:grid-cols-[1fr_40%]"
          tag="section"
        >
          <div>
            <div v-if="$slots.suptitle" class="mb-3">
              <slot name="suptitle" />
            </div>

            <div
              v-if="title || $slots.title"
              class="font-title 3xl:text-6xl/snug text-balance text-3xl/normal lg:text-[42px]/normal"
            >
              <slot name="title">
                <component :is="titleDefinition.tag">
                  {{ titleDefinition.text }}
                </component>
              </slot>
            </div>

            <div
              v-if="subtitle || $slots.subtitle"
              class="mt-4 text-lg md:mt-8"
            >
              <slot name="subtitle">
                <component :is="subtitleDefinition.tag">
                  {{ subtitleDefinition.text }}
                </component>
              </slot>
            </div>

            <div v-if="$slots.default" class="mt-8">
              <slot />
            </div>

            <div
              v-if="$slots.append"
              class="mt-10 [&>[data-app-button]:only-child]:w-full sm:[&>[data-app-button]:only-child]:w-auto"
            >
              <slot name="append" />
            </div>
          </div>

          <div
            v-if="cover || $slots.cover"
            class="relative h-full min-h-[400px] overflow-hidden rounded-xl rounded-br-[theme(spacing.12)] max-lg:hidden"
          >
            <div class="absolute inset-0">
              <slot name="cover">
                <app-image
                  v-if="cover"
                  alt=""
                  class="h-full w-full object-cover"
                  loading="eager"
                  :src="cover"
                  :width="1000"
                />
              </slot>
            </div>
          </div>
        </div>

        <div v-if="$slots.footer" class="mt-10 sm:mt-16">
          <slot name="footer" />
        </div>
      </app-page-wrapper>
    </div>

    <div
      class="relative -mt-6 rounded-t-xl bg-white pt-7 md:-mt-8 md:mb-4 md:rounded-t-[theme(spacing.8)] md:pt-9"
    />
  </template>

  <app-page-wrapper v-else class="md:pb-8" contained-by="content">
    <section
      class="bg-primary-900 grid rounded-xl text-white md:grid-cols-[60%_1fr] md:rounded-br-[theme(spacing.12)]"
    >
      <div class="p-4 md:p-8 lg:px-12">
        <header>
          <div v-if="$slots.suptitle" class="mb-3">
            <slot name="suptitle" />
          </div>

          <div
            v-if="title || $slots.title"
            class="font-title 3xl:text-6xl/snug text-balance text-3xl/normal lg:text-[42px]/normal"
          >
            <slot name="title">
              <component :is="titleDefinition.tag">
                {{ titleDefinition.text }}
              </component>
            </slot>
          </div>

          <div v-if="subtitle || $slots.subtitle" class="mt-3 sm:text-lg">
            <slot name="subtitle">
              <component :is="subtitleDefinition.tag">
                {{ subtitleDefinition.text }}
              </component>
            </slot>
          </div>
        </header>

        <div v-if="$slots.default" class="mt-6">
          <slot />
        </div>

        <footer
          v-if="$slots.append"
          class="mt-10 [&>[data-app-button]:only-child]:w-full sm:[&>[data-app-button]:only-child]:w-auto"
        >
          <slot name="append" />
        </footer>
      </div>
      <div class="pr-6 max-md:hidden lg:pr-8">
        <div
          v-if="cover || $slots.cover"
          class="relative mt-6 size-full overflow-hidden rounded-xl rounded-br-[theme(spacing.12)] shadow-lg lg:mt-8"
        >
          <div class="absolute inset-0">
            <slot name="cover">
              <app-image
                v-if="cover"
                alt=""
                class="aspect-video h-full w-full object-cover"
                loading="eager"
                :src="cover"
                :width="1000"
              />
            </slot>
          </div>
        </div>
      </div>
    </section>
  </app-page-wrapper>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";

const properties = defineProps<{
  title?:
    | I18nMessage
    | { tag: `h${1 | 2 | 3 | 4 | 5 | 6}` | string; text?: I18nMessage };
  subtitle?:
    | I18nMessage
    | { tag: `h${1 | 2 | 3 | 4 | 5 | 6}` | string; text?: I18nMessage };
  cover?: string;
}>();

const newHomeEnabled = useFeatureFlag("new_home");

const titleDefinition = computed(() => {
  if (typeof properties.title === "object") return properties.title;

  return {
    tag: "h1",
    text: properties.title,
  };
});

const subtitleDefinition = computed(() => {
  if (typeof properties.subtitle === "object") return properties.subtitle;

  return {
    tag: "p",
    text: properties.subtitle,
  };
});
</script>
